import { libraryFilterManager } from "@core/service-container";
import { Exercise, isMainExercise } from "@domain/exercise/exercise";
import { AnalyticsViewExerciseDetailOrigin } from "@domain/library/exercise-analytics";
import { ProgramContentEditor } from "@domain/program/program-content-editor";
import { DayIndex } from "@domain/program/program-days";
import { Session } from "@domain/program/session";
import styled from "@emotion/styled";
import { ResponsiveDeviceType } from "@ui/hooks/use-dimensions";
import { DayColumnHeader } from "@ui/pages/program-builder/day-table/day-column-header";
import { DayColumnMenuButton } from "@ui/pages/program-builder/day-table/day-column-menu-button";
import { ExerciseDetailsDialogTabs } from "@ui/pages/program-builder/dialog/exercise-details-dialog";
import { useEditorExerciseCardActions } from "@ui/pages/program-builder/hooks/use-editor-exercise-card-actions";
import { ExerciseCard } from "@ui/pages/program-builder/library/exercise-card";
import { AddExercisesButton } from "@ui/pages/program-builder/mobile/mobile-add-exercises-button";
import { colors } from "@ui/style/colors";
import { useObservable } from "micro-observables";
import React, { useCallback, useState } from "react";

interface MobileDayColumnProps {
	title: string;
	session: Session;
	dayIndex: DayIndex;
	className?: string;
	programEditor: ProgramContentEditor;
}

export const MobileDayColumn = ({
	title,
	session,
	dayIndex,
	className,
	programEditor,
}: MobileDayColumnProps): JSX.Element => {
	const [updatedExerciseId, setUpdatedUpdatedExerciseId] = useState<string | undefined>();
	const editedProgram = useObservable(programEditor.editedProgram);
	const { openExerciseDetailsModal } = useEditorExerciseCardActions();

	//todo Duplicate from day table
	const updateExercise = useCallback(
		async (updatedExercise: Exercise) => {
			isMainExercise(updatedExercise) && programEditor.editExercise(updatedExercise.draggableId, updatedExercise);
			setUpdatedUpdatedExerciseId(updatedExercise.draggableId);
			await libraryFilterManager.clearLibraryFiltersAndSearch();
			setTimeout(() => setUpdatedUpdatedExerciseId(undefined), 1000);
		},
		[programEditor],
	);

	return (
		<Container className={className}>
			<TitleContainer>
				<Title className="p-size-l">{title}</Title>
				{session.exercises.length > 0 && <DayColumnMenuButton editedProgram={editedProgram} dayIndex={dayIndex} />}
			</TitleContainer>
			<DaysContainer>
				<DayColumnHeader session={session} />
				<AddExercisesButton
					onAdd={exercises => {
						const reversedExercises = [...exercises].reverse();
						reversedExercises.forEach(exercise => programEditor.addExerciseToSession(exercise, dayIndex, 0));
					}}
				/>
				{session.exercises.map(exercise => (
					<ExerciseCard
						key={exercise.draggableId}
						exercise={exercise}
						programEditor={programEditor}
						deviceType={ResponsiveDeviceType.Mobile}
						onClick={() =>
							openExerciseDetailsModal(exercise, session.exercises, AnalyticsViewExerciseDetailOrigin.CANVAS)
						}
						onCommentIconClick={() =>
							openExerciseDetailsModal(
								exercise,
								session.exercises,
								AnalyticsViewExerciseDetailOrigin.CANVAS,
								ExerciseDetailsDialogTabs.COMMENT,
							)
						}
						updatedExerciseId={updatedExerciseId}
						updateExercise={updateExercise}
					/>
				))}
				{session.exercises.length > 0 && (
					<AddExercisesButton
						onAdd={exercises => {
							exercises.forEach(exercise =>
								programEditor.addExerciseToSession(
									exercise,
									dayIndex,
									programEditor.getExerciseCountInSession(dayIndex),
								),
							);
						}}
					/>
				)}
			</DaysContainer>
		</Container>
	);
};

const TitleContainer = styled.div`
	display: flex;
	place-items: center;
	padding-left: 13px;
`;

const Title = styled.div`
	margin-top: 10px;
	margin-bottom: 10px;
`;

const Container = styled.div`
	scroll-snap-align: center;
	display: flex;
	flex-direction: column;
	scroll-snap-align: start;
	flex: 1;
`;

const DaysContainer = styled.div`
	background-color: ${colors.primary10};
	display: flex;
	flex-direction: column;
	gap: 8px;
	border-radius: 16px;
	padding: 8px;
	flex: 1;
`;
